import {
  alpha,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ViewListOutlinedIcon from "@mui/icons-material/ViewListOutlined";
import { useQuery } from "@tanstack/react-query";
import { ReactNode, useState } from "react";
import toast from "react-hot-toast";
import { LogSinter } from "../../../interfaces";
import { getLogSinter } from "../../../services/api";
import { getMunicipio } from "../../../services/data";
import {
  ColunaFiltroLogSinter,
  FiltroLogSinter,
  colunasLogSinter,
  formatarDataLogSinter,
  validarFiltroLogSinter,
} from "../../../utils/filtrosLogSinter";

function formatJsonValue(value: unknown): string {
  if (value === undefined || value === null || value === "") return "Sem informação.";
  if (typeof value === "string") {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

export default function TabelaAuditoriaSinter() {
  const municipio = getMunicipio();
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [colunaSelecionada, setColunaSelecionada] = useState<ColunaFiltroLogSinter | "">("");
  const [filtro, setFiltro] = useState("");
  const [filtroAplicado, setFiltroAplicado] = useState<FiltroLogSinter | null>(null);
  const [erroFiltro, setErroFiltro] = useState<string | null>(null);
  const [selectedRecord, setSelectedRecord] = useState<LogSinter | null>(null);
  const [dialogTab, setDialogTab] = useState<"formatado" | "json">("formatado");

  const { data: logSinter, isFetching, isError, refetch } = useQuery({
    queryKey: ["log-sinter-registros", municipio, page, rowsPerPage, filtroAplicado],
    queryFn: ({ signal }) => getLogSinter(page + 1, rowsPerPage, filtroAplicado, signal),
    staleTime: 60_000,
    retry: false,
  });

  const rows = isError ? [] : logSinter?.results ?? [];
  const colunaAtual = colunasLogSinter.find((coluna) => coluna.value === colunaSelecionada);
  const colunaAplicada = colunasLogSinter.find((coluna) => coluna.value === filtroAplicado?.coluna);

  function handleSearch() {
    const erro = validarFiltroLogSinter(colunaSelecionada, filtro);
    setErroFiltro(erro);
    if (erro || !colunaSelecionada) return;
    setFiltroAplicado({ coluna: colunaSelecionada, valor: filtro.trim() });
    setPage(0);
    // Permite repetir a mesma busca, inclusive após uma falha da API.
    if (page === 0 && filtroAplicado?.coluna === colunaSelecionada && filtroAplicado.valor === filtro.trim()) {
      void refetch();
    }
  }

  function clearFilters() {
    setColunaSelecionada("");
    setFiltro("");
    setFiltroAplicado(null);
    setErroFiltro(null);
    setPage(0);
  }

  return (
    <Box sx={{ width: "90%", margin: "auto" }}>
      <Box
        component="form"
        onSubmit={(event) => { event.preventDefault(); handleSearch(); }}
        sx={{ display: "flex", gap: 2, marginBottom: 2, alignItems: "flex-start", flexWrap: "wrap" }}
      >
        <FormControl variant="outlined" sx={{ minWidth: 200 }}>
          <InputLabel id="select-coluna-sinter">Coluna</InputLabel>
          <Select
            labelId="select-coluna-sinter"
            value={colunaSelecionada}
            onChange={(event) => {
              setColunaSelecionada(event.target.value as ColunaFiltroLogSinter);
              setFiltro("");
              setErroFiltro(null);
            }}
            label="Coluna"
          >
            {colunasLogSinter.map((coluna) => (
              <MenuItem key={coluna.value} value={coluna.value}>
                {coluna.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          id="filtro-log-sinter"
          select={colunaSelecionada === "status"}
          label="Filtro"
          variant="outlined"
          value={filtro}
          onChange={(event) => { setFiltro(event.target.value); setErroFiltro(null); }}
          disabled={!colunaSelecionada}
          placeholder={colunaAtual?.placeholder ?? "Selecione uma coluna"}
          helperText={erroFiltro ?? colunaAtual?.ajuda ?? "Selecione a coluna que deseja pesquisar."}
          error={Boolean(erroFiltro)}
          inputProps={{ maxLength: 256, inputMode: colunaSelecionada === "imovel" ? "numeric" : "text" }}
          sx={{ flex: 1, minWidth: 200 }}
        >
          {colunaSelecionada === "status" && [
            <MenuItem key="sucesso" value="sucesso">Sucesso</MenuItem>,
            <MenuItem key="erro" value="erro">Erro</MenuItem>,
          ]}
        </TextField>

        <Button
          color="success"
          type="submit"
          sx={{ width: "143.7px", height: "56px" }}
          variant="contained"
          disabled={!colunaSelecionada || !filtro.trim() || isFetching}
        >
          Filtrar
        </Button>

        <Button
          color="warning"
          onClick={clearFilters}
          sx={{ width: "143.7px", height: "56px" }}
          variant="contained"
        >
          Limpar Filtros
        </Button>
      </Box>

      {filtroAplicado && (
        <Chip
          label={`${colunaAplicada?.label}: ${filtroAplicado.valor}`}
          onDelete={clearFilters}
          sx={{ mb: 2 }}
        />
      )}

      {isError && !isFetching && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" onClick={() => refetch()}>Tentar novamente</Button>}>
          Não foi possível carregar a auditoria SINTER. Tente novamente.
        </Alert>
      )}

      <TableContainer sx={{ overflowX: "auto" }} aria-busy={isFetching}>
        <Table sx={{ minWidth: 980, borderRadius: "8px" }}>
          <TableHead>
            <TableRow>
              {colunasLogSinter.map((coluna) => (
                <TableCell key={coluna.value} sx={{ padding: "16px" }}>
                  {coluna.label}
                </TableCell>
              ))}
              <TableCell sx={{ padding: "16px" }}>Detalhes</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isFetching && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Stack direction="row" gap={1} justifyContent="center" alignItems="center" role="status">
                    <CircularProgress size={20} /> Carregando registros...
                  </Stack>
                </TableCell>
              </TableRow>
            )}
            {!isFetching && !isError && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  {filtroAplicado ? "Nenhum registro encontrado para o filtro aplicado." : "Nenhum registro de auditoria SINTER disponível."}
                </TableCell>
              </TableRow>
            )}
            {!isFetching && rows.map((row) => (
              <TableRow
                key={row.id}
                hover
                onClick={() => setSelectedRecord(row)}
                sx={{ cursor: "pointer" }}
              >
                <TableCell sx={{ padding: "16px", whiteSpace: "nowrap" }}>
                  {formatarDataLogSinter(row.dt_criacao)}
                </TableCell>
                <TableCell sx={{ padding: "16px" }}>{row.numero_cadastro ?? "—"}</TableCell>
                <TableCell sx={{ padding: "16px" }}>{row.inscricao_cartografica ?? "—"}</TableCell>
                <TableCell sx={{ padding: "16px" }}>{row.cib ?? "—"}</TableCell>
                <TableCell sx={{ padding: "16px" }}>{row.tipo ?? "—"}</TableCell>
                <TableCell sx={{ padding: "16px" }}>
                  <StatusChip sucesso={row.sucesso} />
                </TableCell>
                <TableCell sx={{ padding: "16px", maxWidth: 300 }}>
                  {row.sucesso
                    ? "Integração concluída com sucesso."
                    : row.erro
                      ? (Array.isArray(row.erro) ? row.erro.join(", ") : JSON.stringify(row.erro)).slice(0, 120)
                      : "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        labelRowsPerPage="Linhas por página"
        count={isError ? 0 : logSinter?.total_records ?? 0}
        page={logSinter && !isError ? page : 0}
        disabled={isFetching || isError}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
        getItemAriaLabel={(type) => ({ first: "Primeira página", last: "Última página", next: "Próxima página", previous: "Página anterior" })[type]}
        sx={{ marginTop: 2 }}
      />

      <Dialog
        open={Boolean(selectedRecord)}
        onClose={() => { setSelectedRecord(null); setDialogTab("formatado"); }}
        fullWidth
        maxWidth="md"
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: theme.palette.background.paper,
            backgroundImage: "none",
            maxHeight: "85vh",
          },
        }}
      >
        {selectedRecord && (
          <>
            {/* Header */}
            <Box
              sx={{
                px: 3,
                pt: 2.5,
                pb: 0,
                position: "sticky",
                top: 0,
                zIndex: 1,
                bgcolor: theme.palette.background.paper,
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                    Registro #{selectedRecord.id}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                    {selectedRecord.tipo ?? "—"} · {formatarDataLogSinter(selectedRecord.dt_criacao)}
                  </Typography>
                </Box>
                <Stack direction="row" gap={0.5} alignItems="center">
                  <StatusChip sucesso={selectedRecord.sucesso} />
                  <IconButton size="small" onClick={() => { setSelectedRecord(null); setDialogTab("formatado"); }} sx={{ ml: 0.5 }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>

              {/* Info row */}
              <Stack
                direction="row"
                gap={3}
                sx={{
                  mt: 2,
                  pb: 1.5,
                  overflowX: "auto",
                }}
              >
                <MetaItem label="Nº Cadastro" value={selectedRecord.numero_cadastro} />
                <MetaItem label="Inscrição" value={selectedRecord.inscricao_cartografica} mono />
                <MetaItem label="CIB" value={selectedRecord.cib} mono />
              </Stack>

              {/* Toggle */}
              <Stack direction="row" gap={0.5} sx={{ pb: 1 }}>
                <ToggleBtn
                  active={dialogTab === "formatado"}
                  onClick={() => setDialogTab("formatado")}
                  icon={<ViewListOutlinedIcon sx={{ fontSize: 16 }} />}
                  label="Campos"
                />
                <ToggleBtn
                  active={dialogTab === "json"}
                  onClick={() => setDialogTab("json")}
                  icon={<CodeIcon sx={{ fontSize: 16 }} />}
                  label="JSON"
                />
              </Stack>

              <Divider sx={{ mx: -3 }} />
            </Box>

            {/* Content */}
            <DialogContent sx={{ px: 3, py: 2.5 }}>
              {dialogTab === "formatado" ? (
                <FormattedView record={selectedRecord} />
              ) : (
                <JsonView record={selectedRecord} />
              )}
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
}

/* ─── UI primitives ─── */

function StatusChip({ sucesso }: { sucesso?: boolean | null }) {
  return (
    <Chip
      size="small"
      label={sucesso === true ? "Sucesso" : sucesso === false ? "Erro" : "Sem informação"}
      icon={sucesso === true ? <CheckCircleOutlineIcon /> : sucesso === false ? <ErrorOutlineIcon /> : undefined}
      color={sucesso === true ? "success" : sucesso === false ? "error" : "default"}
      variant={sucesso === false ? "filled" : "outlined"}
      sx={{ fontWeight: 500, borderRadius: 1.5 }}
    />
  );
}

function MetaItem(props: { label: string; value: unknown; mono?: boolean }) {
  return (
    <Box sx={{ minWidth: 0, flexShrink: 0 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} noWrap display="block" sx={{ lineHeight: 1.3 }}>
        {props.label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={500}
        noWrap
        sx={{
          mt: 0.15,
          fontFamily: props.mono ? "monospace" : "inherit",
          letterSpacing: props.mono ? 0.3 : 0,
        }}
      >
        {props.value != null && props.value !== "" ? String(props.value) : "—"}
      </Typography>
    </Box>
  );
}

function ToggleBtn(props: { active: boolean; onClick: () => void; icon: ReactNode; label: string }) {
  const theme = useTheme();
  return (
    <Button
      size="small"
      variant={props.active ? "contained" : "text"}
      disableElevation
      startIcon={props.icon}
      onClick={props.onClick}
      sx={{
        borderRadius: 2,
        textTransform: "none",
        fontWeight: 600,
        fontSize: "0.8rem",
        px: 1.5,
        height: 32,
        color: props.active ? undefined : "text.secondary",
        bgcolor: props.active ? undefined : alpha(theme.palette.action.hover, 0.04),
        "&:hover": {
          bgcolor: props.active ? undefined : alpha(theme.palette.action.hover, 0.08),
        },
      }}
    >
      {props.label}
    </Button>
  );
}

/* ─── Helpers ─── */

const FIELD_LABELS: Record<string, string> = {
  inscricaoImobiliaria: "Inscrição imobiliária",
  inscricao_cartografica: "Inscrição cartográfica",
  tipoImovel: "Tipo do imóvel",
  tpArquitetonico: "Tipo arquitetônico",
  areaConstruida: "Área construída",
  areaTerreno: "Área do terreno",
  destinacaoImovel: "Destinação do imóvel",
  anoConstrutivo: "Ano construtivo",
  temBairro: "Possui bairro",
  bice: "BICE",
  padraoConstrutivo: "Padrão construtivo",
  valorVenal: "Valor venal",
  qtdGaragem: "Qtd. garagem",
  temPiscina: "Possui piscina",
  tipoLogradouro: "Tipo de logradouro",
  nomeLogradouro: "Nome do logradouro",
  numeroImovel: "Número",
  complNroImovel: "Complemento nº",
  complEndereco: "Complemento endereço",
  bairro: "Bairro",
  cep: "CEP",
  niTitular: "CPF/CNPJ do titular",
  nomeTitular: "Nome do titular",
  percTitularidade: "% Titularidade",
  dtAquisicaoTitular: "Data aquisição",
  areaPrivativa: "Área privativa",
  areaComum: "Área comum",
  fraIdeal: "Fração ideal",
  nomeServentiaRi: "Serventia RI",
  numMatriculaRi: "Nº matrícula RI",
  cib: "CIB",
  numero_cadastro: "Nº cadastro",
  sucesso: "Sucesso",
  dt_criacao: "Data de criação",
  tipo: "Tipo",
  id_integracao: "ID integração",
  inscricao_imobiliaria: "Inscrição imobiliária",
  cod_servico: "Cód. serviço",
  logradouros: "Logradouros",
  matricula: "Matrícula",
  setor: "Setor",
  quadra: "Quadra",
  lote: "Lote",
  distrito: "Distrito",
  zona_fiscal: "Zona fiscal",
  unidade: "Unidade",
  complemento: "Complemento",
  numero: "Número",
  desativado: "Desativado",
  rural: "Rural",
  latitude: "Latitude",
  longitude: "Longitude",
  camposInvalidos: "Campos inválidos",
  uiIncluida: "UI incluída",
  situacao: "Situação",
  valor: "Valor",
  DadosGeraisImovel: "Dados gerais do imóvel",
  EnderecoImovel: "Endereço do imóvel",
  AreaConstruidaCompl: "Área construída complementar",
  Titular: "Titularidade",
  ServicoRegistroImovel: "Serviço de registro do imóvel",
  CartorioNotas: "Cartório de notas",
  Itbi: "ITBI",
  DadosGeo: "Dados geográficos",
  id: "ID",
  idParcela: "ID da parcela",
  idPonto: "ID do ponto",
  idTestada: "ID da testada",
  idLote: "ID do lote",
  dtUltimoValorVenal: "Dt. último valor venal",
  valorRefMercado: "Valor ref. mercado",
  dataUltVlrMercado: "Dt. último valor mercado",
};

function humanizeKey(key: string): string {
  if (FIELD_LABELS[key]) return FIELD_LABELS[key];
  return key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
}

function fmtValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Sim" : "Não";
  if (typeof value === "number") return value.toLocaleString("pt-BR");
  return String(value);
}

function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function hasContent(v: unknown): boolean {
  if (v === null || v === undefined) return false;
  if (Array.isArray(v)) return v.length > 0;
  if (isObj(v)) return Object.values(v).some((val) => val !== null && val !== undefined && val !== "");
  return true;
}

/* ─── Formatted view ─── */

function SectionHeader(props: { title: string }) {
  const theme = useTheme();
  return (
    <Typography
      variant="overline"
      fontWeight={700}
      sx={{
        letterSpacing: 1,
        color: alpha(theme.palette.text.primary, 0.55),
        mt: 1,
        mb: 0.5,
        display: "block",
      }}
    >
      {props.title}
    </Typography>
  );
}

function FieldRow(props: { label: string; value: ReactNode; mono?: boolean }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        py: 0.75,
        px: 0.5,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
        "&:last-child": { borderBottom: 0 },
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mr: 2, flexShrink: 0, maxWidth: "45%" }}>
        {props.label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={500}
        sx={{
          textAlign: "right",
          wordBreak: "break-word",
          fontFamily: props.mono ? "monospace" : "inherit",
        }}
      >
        {props.value}
      </Typography>
    </Box>
  );
}

function hasDeepContent(v: unknown): boolean {
  if (v === null || v === undefined || v === "") return false;
  if (typeof v !== "object") return true;
  if (Array.isArray(v)) return v.some(hasDeepContent);
  return Object.values(v as Record<string, unknown>).some(hasDeepContent);
}

function NestedSection(props: { label: string; data: Record<string, unknown> }) {
  const theme = useTheme();
  const entries = Object.entries(props.data).filter(([, v]) => hasDeepContent(v));

  return (
    <Box
      sx={{
        mt: 1,
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 0.75,
          bgcolor: alpha(theme.palette.action.hover, 0.03),
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
        }}
      >
        <Typography variant="caption" fontWeight={700} color="text.secondary">
          {props.label}
        </Typography>
      </Box>
      <Box sx={{ px: 1 }}>
        {!entries.length ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            Nenhuma informação encontrada.
          </Typography>
        ) : (
          entries.map(([k, v]) => {
            if (Array.isArray(v)) {
              return v.map((item, i) =>
                isObj(item) ? (
                  <NestedSection key={`${k}-${i}`} label={`${humanizeKey(k)} #${i + 1}`} data={item} />
                ) : (
                  <FieldRow key={`${k}-${i}`} label={humanizeKey(k)} value={fmtValue(item)} />
                ),
              );
            }
            if (isObj(v)) {
              return <NestedSection key={k} label={humanizeKey(k)} data={v} />;
            }
            return <FieldRow key={k} label={humanizeKey(k)} value={fmtValue(v)} />;
          })
        )}
      </Box>
    </Box>
  );
}

function RenderFields(props: { data: unknown }) {
  const { data } = props;

  if (!hasContent(data)) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
        Sem informação.
      </Typography>
    );
  }

  if (Array.isArray(data)) {
    return (
      <Stack gap={1}>
        {data.map((item, i) =>
          isObj(item) ? <RenderFields key={i} data={item} /> : (
            <Typography key={i} variant="body2">{fmtValue(item)}</Typography>
          ),
        )}
      </Stack>
    );
  }

  if (!isObj(data)) {
    return <Typography variant="body2">{fmtValue(data)}</Typography>;
  }

  const simple: [string, unknown][] = [];
  const nested: [string, unknown][] = [];

  Object.entries(data).forEach(([k, v]) => {
    if (v === null || v === undefined || v === "") return;
    if (isObj(v) || Array.isArray(v)) {
      nested.push([k, v]);
    } else {
      simple.push([k, v]);
    }
  });

  if (!simple.length && !nested.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
        Nenhuma informação encontrada.
      </Typography>
    );
  }

  return (
    <>
      {simple.map(([k, v]) => (
        <FieldRow key={k} label={humanizeKey(k)} value={fmtValue(v)} />
      ))}
      {nested.map(([k, v]) => {
        if (Array.isArray(v)) {
          return v.map((item, i) =>
            isObj(item) ? (
              <NestedSection key={`${k}-${i}`} label={`${humanizeKey(k)} #${i + 1}`} data={item} />
            ) : (
              <FieldRow key={`${k}-${i}`} label={humanizeKey(k)} value={fmtValue(item)} />
            ),
          );
        }
        if (isObj(v)) {
          return <NestedSection key={k} label={humanizeKey(k)} data={v} />;
        }
        return null;
      })}
    </>
  );
}

function FormattedView(props: { record: LogSinter }) {
  const { record } = props;
  const theme = useTheme();

  const sections: { title: string; data: unknown; show: boolean }[] = [
    { title: "Input (Payload Enviado)", data: record.input, show: hasContent(record.input) },
    { title: "Output (Resposta)", data: record.output, show: hasContent(record.output) },
    { title: "Erro", data: record.erro, show: hasContent(record.erro) },
    { title: "Dados do Imóvel", data: record.imovel, show: hasContent(record.imovel) },
    { title: "Resposta Final", data: record.final_response, show: hasContent(record.final_response) },
  ];

  return (
    <Stack gap={0.5}>
      {sections.map((s) => (
        <Box key={s.title}>
          <SectionHeader title={s.title} />
          <Box
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              px: 1.5,
              py: 0.5,
              bgcolor: alpha(theme.palette.background.default, theme.palette.mode === "dark" ? 0.25 : 0.4),
            }}
          >
            {s.show ? (
              <RenderFields data={s.data} />
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1.5 }}>
                Nenhuma informação encontrada.
              </Typography>
            )}
          </Box>
        </Box>
      ))}
    </Stack>
  );
}

/* ─── JSON view ─── */

function JsonView(props: { record: LogSinter }) {
  const { record } = props;
  const theme = useTheme();

  const sections: { title: string; data: unknown }[] = [
    { title: "Input", data: record.input },
    { title: "Output", data: record.output },
    { title: "Erro", data: record.erro },
    { title: "Imóvel", data: record.imovel },
    { title: "Resposta Final", data: record.final_response },
  ];

  function handleCopy(data: unknown) {
    navigator.clipboard.writeText(formatJsonValue(data)).then(
      () => toast.success("JSON copiado!"),
      () => toast.error("Erro ao copiar."),
    );
  }

  return (
    <Stack gap={2}>
      {sections.map((s) => (
        <Box key={s.title}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography variant="overline" fontWeight={700} sx={{ letterSpacing: 1, color: alpha(theme.palette.text.primary, 0.55) }}>
              {s.title}
            </Typography>
            {hasContent(s.data) && (
              <Tooltip title="Copiar JSON">
                <IconButton size="small" onClick={() => handleCopy(s.data)} sx={{ color: "text.secondary" }}>
                  <ContentCopyIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 2,
              maxHeight: 300,
              overflow: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              bgcolor: alpha(theme.palette.background.default, theme.palette.mode === "dark" ? 0.35 : 0.5),
              color: "text.primary",
              fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
              fontSize: "0.78rem",
              lineHeight: 1.65,
            }}
          >
            {formatJsonValue(s.data)}
          </Box>
        </Box>
      ))}
    </Stack>
  );
}
